import { Helmet } from 'react-helmet-async';

import {
  DEFAULT_OG_IMAGE,
  SITE_LOCALE,
  SITE_NAME,
  absoluteUrl,
} from '@/config/site';

import JsonLd from './JsonLd';

export interface SeoProps {
  /** Page title (used verbatim for <title>, og:title and twitter:title). */
  title: string;
  /** Meta description (used for description, og:description, twitter:description). */
  description: string;
  /** Canonical path for this page, e.g. '/science'. Resolved against SITE_URL. */
  path: string;
  /** Social share image — a path on SITE_URL or an absolute URL. Defaults to the
   *  site-wide social image. */
  image?: string;
  /** og:type. Defaults to 'website'. */
  type?: string;
  /** When true, emit noindex/nofollow so search engines skip the page. */
  noindex?: boolean;
  /** JSON-LD structured-data objects to embed in the document head. */
  jsonLd?: object | object[];
}

/**
 * The single SEO helper for every public page. Emits a consistent set of
 * title/description/canonical, Open Graph and Twitter Card tags — all resolved
 * against the single SITE_URL constant — plus any structured data.
 *
 * Robots is owned here (not by a static tag in index.html) so it is always a
 * single authoritative directive per page: `noindex` pages get noindex/nofollow;
 * otherwise the environment default is honoured (VITE_ROBOTS_CONTENT keeps
 * non-production builds out of the index), falling back to index/follow.
 */
export default function Seo({
  title,
  description,
  path,
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  noindex = false,
  jsonLd,
}: SeoProps) {
  const url = absoluteUrl(path);
  const imageUrl = absoluteUrl(image);
  const robots = noindex
    ? 'noindex, nofollow'
    : import.meta.env.VITE_ROBOTS_CONTENT || 'index, follow';

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content={robots} />
        <link rel="canonical" href={url} />

        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content={type} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:locale" content={SITE_LOCALE} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imageUrl} />
      </Helmet>

      {jsonLd && <JsonLd data={jsonLd} />}
    </>
  );
}
